import type { ReactElement } from 'react';
import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import { DEFAULT_PRODUCT_NAME } from '../../../../md-config';
import { IMAGES_LOGOTYPES_MIN_DOKTOR_LOGOTYPE_BLACK_SVG } from '../../../autogenerated/resources.autogenerated';
import { assertExists } from '../../../utils/codingUtil';
import { Image } from '../../primitives/Image';
import { Link } from '../../primitives/Link';

import * as classes from './BasicPageFooter.module.scss';

export const BasicPageFooter = (): ReactElement => {
  const data = useStaticQuery<Queries.BasicPageFooterQuery>(graphql`
    query BasicPageFooter {
      level1s: allDatoCmsDataFooterLinksLevel1 {
        edges {
          node {
            id
            label
          }
        }
      }

      level2s: allDatoCmsDataFooterLinksLevel2 {
        edges {
          node {
            parentLevel {
              id
            }
            label
            link
          }
        }
      }
    }
  `);

  const groups: Group[] = [];
  for (const level1edge of data.level1s.edges) {
    const links: GroupLink[] = [];
    for (const level2edge of data.level2s.edges) {
      if (level2edge.node.parentLevel?.id === level1edge.node.id) {
        links.push({
          label: assertExists(level2edge.node.label),
          link: assertExists(level2edge.node.link),
        });
      }
    }
    groups.push({
      label: assertExists(level1edge.node.label),
      links,
    });
  }

  return (
    <div className={classes.footer}>
      <div className={classes.footer__inner}>
        <div className={classes.footer__inner__column}>
          <Link alt={DEFAULT_PRODUCT_NAME} href="/">
            <Image
              src={IMAGES_LOGOTYPES_MIN_DOKTOR_LOGOTYPE_BLACK_SVG}
              title={DEFAULT_PRODUCT_NAME}
              alt="Min Doktor logotyp"
              width={130}
              height={28}
              className={classes.footer__inner__column__mdLogotype}
              loading="lazy"
            />
          </Link>

          <div
            className={classes.footer__inner__column__regionSormlandContainer}
          >
            <div
              className={
                classes.footer__inner__column__regionSormlandContainer__text
              }
            >
              Min Doktor erbjuder digital primärvård som ingår i det fria
              vårdvalet via underleverantörsavtal hos Min Doktor Vårdcentral &
              BVC i Sörmland.
            </div>
          </div>

          <div className={classes.footer__inner__column__legitscriptContainer}>
            <Link
              href="https://www.legitscript.com/websites/?checker_keywords=mindoktor.se"
              alt="Verify LegitScript Approval"
              target="_blank"
              rel="noreferrer nofollow"
            >
              <Image
                src="https://static.legitscript.com/seals/11754100.png"
                title="LegitScript Approval"
                alt="LegitScript Approval"
                width={140}
                height={120}
                loading="lazy"
              />
            </Link>
          </div>
        </div>

        {groups.map((group, i) => (
          <div key={i} className={classes.footer__inner__column}>
            <div className={classes.footer__inner__column__header}>
              {group.label}
            </div>
            {group.links.map((link, li) => (
              <Link
                key={li}
                classNames={[classes.footer__inner__column__link]}
                href={link.link}
                alt={link.label}
              >
                {link.label}
              </Link>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

interface Group {
  label: string;
  links: GroupLink[];
}

interface GroupLink {
  label: string;
  link: string;
}
